import { Component } from '@angular/core';

@Component({
  selector: 'app-about',
  templateUrl: './about.component.html',
  styleUrls: ['./about.component.scss']
})
export class AboutComponent {
  selectedOwnerIndex = 0;
  selectedRouteStopIndex = 0;

  private readonly fieldJournalImageMap: Record<string, string> = {
    'assets/images/kari_and_roland_rr.jpg': 'assets/images/design-a/kari-roland-georgetown-field-journal.jpg',
    'assets/images/kari_roland_door.jpg': 'assets/images/design-a/kari-roland-door-field-journal.jpg',
    'assets/images/roland_bridge_standing.jpg': 'assets/images/design-a/roland-bridge-standing-field-journal.jpg',
    'assets/images/kari_bridge.jpg': 'assets/images/design-a/kari-bridge-field-journal.jpg'
  };




  proofPoints = [
    { value: '21+', label: 'Years as a senior project manager' },
    { value: '2', label: 'Years as a general superintendent' },
    { value: '2011', label: 'Kari serving Georgetown community since' },
    { value: '6', label: 'Days a week available for inspections' }
  ];

  ownerCareCards = [
    {
      role: 'Kari',
      title: 'Friendly scheduling and request details',
      description: 'Kari helps turn addresses, timing, access notes, and contact preferences into a clear next step.',
      image: 'assets/images/kari_headshot.jpg',
      imagePosition: 'center 18%',
      cue: 'Before the appointment'
    },
    {
      role: 'Roland',
      title: 'Careful inspection and clear explanation',
      description: 'Roland takes time on site, documents visible conditions, and explains what the report means.',
      image: 'assets/images/roland_bridge_standing.jpg',
      imagePosition: 'center 18%',
      cue: 'During the inspection'
    },
    {
      role: 'Together',
      title: 'A local Georgetown handoff',
      description: 'GTX keeps the process personal, professional, and easy to understand before and after the report.',
      image: 'assets/images/kari_and_roland_rr.jpg',
      imagePosition: 'center 42%',
      cue: 'After the report'
    }
  ];

  ownerSignals = [
    {
      title: 'Georgetown-based and approachable',
      description: 'Roland and Kari keep GTX personal, responsive, and rooted in Central Texas neighborhoods.'
    },
    {
      title: 'Reports prepared for real property decisions',
      description: 'Roland does not rush inspections because the report needs to support a real home decision.'
    },
    {
      title: 'Scheduling and follow-up stay human',
      description: 'Kari helps clients know what to send, what happens next, and how GTX will respond.'
    }
  ];

  ownerRouteStops = [
    {
      label: 'Before the inspection',
      lead: 'Kari coordinates the plan',
      title: 'Scheduling starts with a local person, not a generic intake desk.',
      description: 'Kari helps turn the property address, timing, access details, and inspection question into a clear next step before Roland arrives.',
      image: 'assets/images/kari_roland_door.jpg',
      imagePosition: 'center center',
      details: ['Scheduling details', 'Contact preferences', 'Property and access notes']
    },
    {
      label: 'During the walk-through',
      lead: 'Roland documents the home',
      title: 'Roland takes time on site so the report supports a real decision.',
      description: 'Roland reviews visible systems carefully, answers questions when appropriate, and explains what the findings mean in plain language.',
      image: 'assets/images/roland_bridge_standing.jpg',
      imagePosition: 'center 18%',
      details: ['TREC# 23635', 'Construction background', 'Spanish-speaking support']
    },
    {
      label: 'After the report',
      lead: 'GTX keeps the experience clear',
      title: 'The report and follow-up are organized around what clients need next.',
      description: 'GTX focuses on useful explanations, practical homeowner context, and a professional record buyers, sellers, owners, and agents can understand.',
      image: 'assets/images/kari_and_roland_rr.jpg',
      imagePosition: 'center 42%',
      details: ['Clear report language', 'Local context', 'Responsive follow-up']
    }
  ];

  ownerProofCards = [
    {
      title: 'A real local response',
      description: 'Roland and Kari support Georgetown and nearby Central Texas clients directly.'
    },
    {
      title: 'Careful reports, not rushed visits',
      description: 'GTX keeps the inspection focused on accurate observations and useful client education.'
    },
    {
      title: 'Family-friendly communication',
      description: 'The tone stays professional, approachable, and clear before, during, and after the appointment.'
    }
  ];

  ownerCommitments = [
    {
      title: 'Roland takes detailed inspection notes',
      description: 'Inspections are not rushed because every home deserves careful attention and a clear explanation.'
    },
    {
      title: 'Kari keeps communication organized',
      description: 'Scheduling, follow-up, and client support are handled with a friendly local-business touch.'
    },
    {
      title: 'Clients leave with a usable record',
      description: 'GTX focuses on education so buyers, sellers, homeowners, and agents can make confident decisions.'
    }
  ];

  localMarkers = [
    'Georgetown community involvement',
    'Family-friendly communication',
    'Spanish-speaking inspection support',
    'Central Texas service area'
  ];

  ownerProfiles = [
    {
      label: 'Roland',
      role: 'Lead inspector · TREC# 23635',
      title: 'Careful inspection observations with a construction and planning background.',
      image: 'assets/images/roland_bridge_standing.jpg',
      imagePosition: 'center 18%',
      clientPromise: 'A careful walk-through, plain-language explanation, and time spent answering client and agent questions.',
      localNote: 'Roland takes the time to connect visible findings to the practical questions buyers, sellers, owners, and agents need answered across Central Texas homes.',
      description: 'Roland leads the inspection, takes time on site, and explains visible conditions so clients and agents can make informed decisions.',
      details: [
        '21 years as a senior project manager',
        '2 years as a general superintendent',
        'Professional home inspector',
        'Fluent in Spanish'
      ]
    },
    {
      label: 'Kari',
      role: 'Owner and administrator',
      title: 'Organized client support from first property request to follow-up.',
      image: 'assets/images/kari_bridge.jpg',
      imagePosition: 'center 20%',
      clientPromise: 'Friendly scheduling communication, organized request details, and a local service mindset.',
      localNote: 'Kari helps the appointment feel calm by keeping property details, scheduling questions, and follow-up communication organized.',
      description: 'Kari brings administration experience and a long record of Georgetown community service to the scheduling and client support side of GTX.',
      details: [
        'Georgetown community service since 2011',
        'Administration background',
        'Local school and booster volunteer work',
        'Friendly scheduling support'
      ]
    },
    {
      label: 'Together',
      role: 'Local family business',
      title: 'Professional inspection work with a neighborly Central Texas tone.',
      image: 'assets/images/kari_and_roland_rr.jpg',
      imagePosition: 'center 42%',
      clientPromise: 'A Georgetown-owned inspection experience that feels clear, professional, and easy to talk through.',
      localNote: 'Together, Roland and Kari keep GTX grounded in Georgetown while serving Central Texas clients with clear reports and approachable communication.',
      description: 'Together, Roland and Kari make GTX feel approachable while still keeping the report accurate, clear, and useful for Central Texas homeowners.',
      details: [
        'Locally owned in Georgetown',
        'Licensed and bonded',
        'Clear client education',
        'Central Texas service area'
      ]
    }
  ];

  fieldJournalTeamMarkers = [
    'Georgetown-owned',
    'TREC# 23635',
    'Licensed and bonded',
    'Central Texas'
  ];

  get fieldJournalOwnerProfiles() {
    return this.ownerProfiles.slice(0, 2);
  }

  selectOwner(index: number): void {
    this.selectedOwnerIndex = index;
  }

  selectRouteStop(index: number): void {
    this.selectedRouteStopIndex = index;
  }

  get selectedOwner() {
    return this.ownerProfiles[this.selectedOwnerIndex];
  }

  get selectedFieldJournalOwnerIndex(): number {
    return Math.min(this.selectedOwnerIndex, this.fieldJournalOwnerProfiles.length - 1);
  }

  get selectedFieldJournalOwner() {
    return this.fieldJournalOwnerProfiles[this.selectedFieldJournalOwnerIndex];
  }

  get selectedRouteStop() {
    return this.ownerRouteStops[this.selectedRouteStopIndex];
  }

  fieldJournalImage(image: string): string {
    return this.fieldJournalImageMap[image] || image;
  }

}
